/**
 * =============================================================================
 * ExperienceCostsComponent - Dashboard Widget for Experience Pricing
 * =============================================================================
 *
 * Compact dashboard component for viewing experience costs at a glance.
 * Uses the new per-class pricing model with dynamic cost components.
 *
 * For full CRUD operations, use the ExperiencePricing page.
 *
 * @author United Artificial Intelligence Company, Inc.
 * @version 2.0.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
    LinearProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { listPricingCosts } from '../../services/pricingProxyService';

const API_BASE = "https://api.whook.ai";
const brandPink = "#C63D57";
const brandGreen = "#12c099";

// Tag colors
const TAG_COLORS = {
    wellness: '#12c099',
    fitness: '#C63D57',
    sports: '#3d8bc6',
    mindfulness: '#9c27b0',
    'team-building': '#ff9800',
    creative: '#e91e63',
    virtual: '#00bcd4'
};

const ExperienceCostsComponent = ({ onViewAll }) => {
    const [costs, setCosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCosts();
    }, []);

    const fetchCosts = async () => {
        setLoading(true);
        setError(null);
        try {
            // Admin read through the mgmt-backend proxy; whook gates it behind the internal key
            const data = await listPricingCosts();
            if (data.success) {
                setCosts(data.costs || []);
            }
        } catch (err) {
            // Fallback to public endpoint
            try {
                const fallback = await axios.get(`${API_BASE}/v1/agent/pricing/sports`);
                if (fallback.data.success) {
                    setCosts(fallback.data.sports || []);
                }
            } catch (e) {
                setError('Failed to load pricing data');
            }
        } finally {
            setLoading(false);
        }
    };

    // Stats
    const stats = useMemo(() => {
        if (!costs.length) return { total: 0, active: 0, avgPrice: 0, avgMargin: 0, highestMargin: null, lowestMargin: null };

        const active = costs.filter(c => c.isActive !== false);
        const avgPrice = Math.round(active.reduce((sum, c) => sum + (c.pricePerClass || 0), 0) / active.length);
        const avgMargin = Math.round(active.reduce((sum, c) => sum + ((c.targetMargin || 0.75) * 100), 0) / active.length);

        // Find highest/lowest margin experiences
        const sorted = [...active].sort((a, b) => (b.targetMargin || 0.75) - (a.targetMargin || 0.75));
        const highestMargin = sorted[0];
        const lowestMargin = sorted[sorted.length - 1];

        return {
            total: costs.length,
            active: active.length,
            avgPrice,
            avgMargin,
            highestMargin,
            lowestMargin
        };
    }, [costs]);

    // Get primary tag color
    const getTagColor = (tags) => {
        if (!tags?.length) return '#666';
        return TAG_COLORS[tags[0]] || '#666';
    };

    // Top 8 experiences by price (for dashboard view)
    const topExperiences = useMemo(() => {
        return [...costs]
            .filter(c => c.isActive !== false)
            .sort((a, b) => (b.pricePerClass || 0) - (a.pricePerClass || 0))
            .slice(0, 8);
    }, [costs]);

    return (
        <Box sx={{
            bgcolor: '#1e1e1e',
            borderRadius: 3,
            p: 3,
            border: '1px solid #333',
            height: '100%'
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                        Experience Pricing
                    </Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                        Per-class pricing model
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchCosts} size="small" sx={{ color: '#888' }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {onViewAll && (
                        <Button
                            size="small"
                            endIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={onViewAll}
                            sx={{ color: brandGreen, fontSize: '0.75rem' }}
                        >
                            Manage All
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: brandGreen }} />
                </Box>
            ) : (
                <>
                    {/* Quick Stats */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Box sx={{ flex: 1, bgcolor: '#141414', borderRadius: 2, p: 2, textAlign: 'center' }}>
                            <Typography sx={{ color: '#666', fontSize: '0.65rem', letterSpacing: 0.5 }}>EXPERIENCES</Typography>
                            <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700 }}>{stats.active}</Typography>
                            <Typography sx={{ color: brandGreen, fontSize: '0.7rem' }}>active</Typography>
                        </Box>
                        <Box sx={{ flex: 1, bgcolor: '#141414', borderRadius: 2, p: 2, textAlign: 'center' }}>
                            <Typography sx={{ color: '#666', fontSize: '0.65rem', letterSpacing: 0.5 }}>AVG PRICE</Typography>
                            <Typography sx={{ color: brandPink, fontSize: '1.5rem', fontWeight: 700 }}>${stats.avgPrice}</Typography>
                            <Typography sx={{ color: '#666', fontSize: '0.7rem' }}>per class</Typography>
                        </Box>
                        <Box sx={{ flex: 1, bgcolor: '#141414', borderRadius: 2, p: 2, textAlign: 'center' }}>
                            <Typography sx={{ color: '#666', fontSize: '0.65rem', letterSpacing: 0.5 }}>AVG MARGIN</Typography>
                            <Typography sx={{ color: brandGreen, fontSize: '1.5rem', fontWeight: 700 }}>{stats.avgMargin}%</Typography>
                            <Typography sx={{ color: '#666', fontSize: '0.7rem' }}>target</Typography>
                        </Box>
                    </Box>

                    {/* Margin Range Indicator */}
                    {stats.highestMargin && stats.lowestMargin && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#141414', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ color: '#888', fontSize: '0.7rem' }}>
                                    {stats.lowestMargin.displayName || stats.lowestMargin.sportName}: {Math.round((stats.lowestMargin.targetMargin || 0.75) * 100)}%
                                </Typography>
                                <Typography sx={{ color: '#888', fontSize: '0.7rem' }}>
                                    {stats.highestMargin.displayName || stats.highestMargin.sportName}: {Math.round((stats.highestMargin.targetMargin || 0.75) * 100)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={stats.avgMargin}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: '#333',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: brandGreen,
                                        borderRadius: 3
                                    }
                                }}
                            />
                            <Typography sx={{ color: '#666', fontSize: '0.65rem', mt: 0.5, textAlign: 'center' }}>
                                Margin range across all experiences
                            </Typography>
                        </Box>
                    )}

                    {/* Compact Table */}
                    <TableContainer component={Paper} sx={{ bgcolor: '#141414', maxHeight: 320 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ bgcolor: '#1e1e1e', color: '#888', fontSize: '0.7rem', py: 1 }}>Experience</TableCell>
                                    <TableCell sx={{ bgcolor: '#1e1e1e', color: '#888', fontSize: '0.7rem', py: 1 }} align="right">Cost</TableCell>
                                    <TableCell sx={{ bgcolor: '#1e1e1e', color: '#888', fontSize: '0.7rem', py: 1 }} align="right">Margin</TableCell>
                                    <TableCell sx={{ bgcolor: '#1e1e1e', color: '#888', fontSize: '0.7rem', py: 1 }} align="right">Price</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {topExperiences.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ color: '#666', textAlign: 'center', py: 4, fontSize: '0.8rem' }}>
                                            No experiences configured
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    topExperiences.map((cost) => {
                                        const margin = Math.round((cost.targetMargin || 0.75) * 100);
                                        return (
                                            <TableRow key={cost._id || cost.category} hover sx={{ '&:hover': { bgcolor: '#1a1a1a' } }}>
                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ fontSize: '1rem' }}>{cost.icon || '🏃'}</Typography>
                                                        <Box>
                                                            <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.2 }}>
                                                                {cost.displayName || cost.sportName}
                                                            </Typography>
                                                            {cost.tags?.length > 0 && (
                                                                <Typography sx={{ color: getTagColor(cost.tags), fontSize: '0.65rem' }}>
                                                                    {cost.tags[0]}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#888', fontSize: '0.8rem', py: 1.5 }}>
                                                    ${cost.costPerClass || 0}
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 1.5 }}>
                                                    <Chip
                                                        label={`${margin}%`}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                            bgcolor: margin >= 60 ? 'rgba(18,192,153,0.2)' : margin >= 40 ? 'rgba(255,152,0,0.2)' : 'rgba(198,61,87,0.2)',
                                                            color: margin >= 60 ? brandGreen : margin >= 40 ? '#ff9800' : brandPink
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 1.5 }}>
                                                    <Typography sx={{ color: brandPink, fontSize: '0.85rem', fontWeight: 700 }}>
                                                        ${cost.pricePerClass || 0}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Footer */}
                    {costs.length > 8 && (
                        <Typography sx={{ color: '#666', fontSize: '0.7rem', mt: 1.5, textAlign: 'center' }}>
                            Showing top 8 of {costs.length} experiences by price
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
};

export default ExperienceCostsComponent;